import { useAppForm } from "@features/form/useAppForm";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { revalidateLogic } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { authClient } from "./authClient";
import { AuthRoute } from "./AuthRoute";
import type { InvitationJoinProps } from "./AuthRoute";
import { InvitationJoinCheckbox } from "./InvitationJoinCheckbox";
import { SocialSignInAndUp } from "./SocialSignInUp";

type SignUpPageProps = {
	email: string;
	onChangeEmail: ((email: string) => void) | undefined;
	invitationJoin?: InvitationJoinProps;
};

export const SignUpPage = (props: SignUpPageProps) => {
	const { email, onChangeEmail, invitationJoin } = props;
	const LL = useI18nContext().LL.auth;

	const navigate = useNavigate();
	const { callbackUrl } = AuthRoute.useLoaderData();
	const { show, redirectToPath } = AuthRoute.useSearch();

	const SignUpSchema = useMemo(
		() =>
			z.object({
				email: z.email(),
				password: z.string().min(8, LL.FORM_FIELDS_PASSWORD_ERROR_MESSAGE()),
				terms: z.boolean().refine((v) => v === true, { message: LL.FORM_FIELDS_TOS_REQUIRED() }),
				newsletter: z.boolean().optional(),
			}),
		[LL]
	);

	const form = useAppForm({
		defaultValues: {
			email: email,
			password: "",
			terms: false,
			newsletter: false,
		} as z.infer<typeof SignUpSchema>,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: SignUpSchema,
		},
		onSubmit: async ({ value }) => {
			try {
				const res = await authClient.signUp.email({
					email: value.email,
					password: value.password,
					name: "",
					acceptedNewsletter: value.newsletter || false,
					acceptedPrivacyPolicyVersion: 1,
					acceptedTermsOfServiceVersion: 1,
					callbackURL: callbackUrl,
				});
				console.log(res);
				if (res.error) {
					throw res.error;
				}
				if (res.data.user.emailVerified) {
					navigate({ to: redirectToPath });
				} else {
					// Preserve the existing search params (invitationId, redirectToPath, ...)
					// so the invitation survives the email-verification step.
					return navigate({ from: AuthRoute.fullPath, search: (prev) => ({ ...prev, show, verifyEmail: value.email }) });
				}
			} catch (error: any) {
				console.error(error);
				toast.error("message" in error ? (error.message as string) : "Something went wrong, try again later");
			}
		},
	});

	return (
		<div className="m-auto w-full max-w-sm space-y-6">
			{/* Header */}
			<div className="space-y-2 text-center">
				<h1 className="mb-3 text-2xl font-bold md:text-3xl font-breit">
					<T>{LL.SIGN_UP_TITLE()}</T>
				</h1>
				<p className="text-muted-foreground text-sm">
					<T>{LL.SIGN_UP_DESCRIPTION()}</T>
				</p>
			</div>

			<SocialSignInAndUp />

			{/* Sign-up form fields */}
			<form
				id="sign-up"
				className="mb-6 space-y-4"
				onSubmit={(e) => {
					console.log("submit");
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<form.AppField
					name="email"
					listeners={{
						onChange: ({ value }) => {
							onChangeEmail?.(value);
						},
					}}
				>
					{(field) => <field.Input label={LL.FORM_FIELDS_EMAIL_LABEL()} placeholder={LL.FORM_FIELDS_EMAIL_PLACEHOLDER()} />}
				</form.AppField>

				{/* Password input */}
				<div className="space-y-2">
					<form.AppField name="password">
						{(field) => (
							<field.Password
							label={LL.FORM_FIELDS_PASSWORD_LABEL()}
							placeholder={LL.FORM_FIELDS_PASSWORD_PLACEHOLDER()}
							autoComplete="new-password"
							description={LL.FORM_FIELDS_PASSWORD_DESCRIPTION()}
							/>
						)}
					</form.AppField>
				</div>
				{/* Terms and conditions checkbox */}
				<form.AppField name="terms">
					{(field) => (
						<field.Checkbox label={LL.FORM_FIELDS_TOS_LABEL()} description={LL.FORM_FIELDS_TOS_DESCRIPTION()} hideLabel />
					)}
				</form.AppField>
				<form.AppField name="newsletter">
					{(field) => (
						<field.Checkbox
							label={LL.FORM_FIELDS_MARKETING_CONSENT_LABEL()}
							description={LL.FORM_FIELDS_MARKETING_CONSENT_DESCRIPTION()}
							hideLabel
						/>
					)}
				</form.AppField>
			</form>

			{invitationJoin && <InvitationJoinCheckbox {...invitationJoin} />}

			<div className="flex flex-col space-y-4">
				<form.AppForm>
					<form.Subscribe selector={(state) => ({ terms: state.values.terms, isSubmitting: state.isSubmitting })}>
						{({ terms }) => (
							<form.SubmitButton form="sign-up" className="w-full" disabled={!terms}>
								<T>{LL.SIGN_UP_SUBMIT_BUTTON()}</T>
							</form.SubmitButton>
						)}
					</form.Subscribe>
				</form.AppForm>
				<Link className="text-foreground underline" to="/auth" search={{ show: "sign-in" }}>
					<p className="text-center text-sm">
						<T>{LL.SIGN_UP_ALREADY_HAVE_ACCOUNT_TEXT()}</T>
					</p>
				</Link>
			</div>
		</div>
	);
};
